/**
 * ReceiptGenerator.js
 * 
 * Generates the EXACT same HTML receipt as the Android app's generateReceiptHtml().
 * Supports: Print-to-PDF (browser native), Web Share API, and preview modal.
 */

// --- Payment category formatter (mirrors Android's formatPaymentCategory) ---
export function formatPaymentCategory(category) {
    switch (category) {
        case 'self_payment': return 'Self Payment';
        case 'friend_payment': return 'Friend Payment';
        case 'loan_partial_self_payment': return 'Partial Self Payment';
        case 'loan_partial_friend_payment': return 'Partial Friend Payment';
        default: return category ? category.replace(/_/g, ' ') : 'N/A';
    }
}

// --- Format Firestore timestamp or Date ---
function formatDate(timestamp) {
    let d;
    if (timestamp && timestamp.seconds) {
        d = new Date(timestamp.seconds * 1000);
    } else if (timestamp instanceof Date) {
        d = timestamp;
    } else {
        d = new Date();
    }
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Generate the full receipt HTML string.
 * Mirrors Android's generateReceiptHtml() exactly.
 */
export function generateReceiptHtml(bill, userData, monthYear) {
    const date = formatDate(new Date());
    const fineAmount = ((bill.fineAmount || 0) / 1).toFixed(2);

    const loanDeduction = (bill.loanDeductionApplied || 0);
    const cashPaid = (bill.paidAmount || 0);
    const totalEffective = cashPaid + loanDeduction;

    const paidAmount = totalEffective.toFixed(2);
    const loanFormatted = loanDeduction.toFixed(2);
    const cashFormatted = cashPaid.toFixed(2);

    const logoUrl = window.location.origin + '/assets/ic_placeholder_avatar.png';

    const loanSection = loanDeduction > 0 ? `
                <div class="info-row">Loan Adjusted: <span class="info-val">₹${loanFormatted}</span></div>
                <div class="info-row">Cash Paid: <span class="info-val">₹${cashFormatted}</span></div>
  ` : '';

    const fineRow = (bill.fineAmount || 0) > 0 ? `
                <tr>
                    <td>Late Payment Fine</td>
                    <td class="amount-col">₹${fineAmount}</td>
                </tr>
  ` : '';

    return `
    <html>
    <head>
        <meta name="viewport" content="width=595, initial-scale=1.0, user-scalable=no">
        <style>
            body {
                width: 595px;
                margin: 0 auto;
                padding: 40px;
                box-sizing: border-box;
                font-family: 'Helvetica', sans-serif;
                color: #333;
                background-color: #fff;
            }
            .header-container {
                display: flex;
                align-items: center;
                justify-content: space-between;
                margin-bottom: 40px;
                padding-bottom: 20px;
                border-bottom: 4px solid #F59E0B;
            }
            .logo-section {
                flex: 0 0 100px;
            }
            .logo-img {
                width: 80px;
                height: 80px;
                border-radius: 12px;
                image-rendering: pixelated;
            }
            .title-section {
                text-align: right;
            }
            .title-section h1 { margin: 0; color: #0F172A; font-size: 28px; text-transform: uppercase; letter-spacing: 1px; }
            .title-section h2 { margin: 5px 0 0 0; color: #F59E0B; font-size: 16px; font-weight: 600; }
            .title-section p { margin: 5px 0 0 0; color: #64748B; font-size: 12px; }
            .content-grid {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 30px;
                margin-bottom: 30px;
            }
            .box-title {
                font-size: 12px;
                color: #94A3B8;
                font-weight: bold;
                text-transform: uppercase;
                margin-bottom: 8px;
                border-bottom: 1px solid #E2E8F0;
                padding-bottom: 4px;
            }
            .info-row { margin-bottom: 6px; font-size: 14px; }
            .info-label { color: #64748B; display: inline-block; width: 80px; }
            .info-val { color: #0F172A; font-weight: 600; }
            .bill-table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 20px;
            }
            .bill-table th {
                text-align: left;
                padding: 12px;
                background-color: #F8FAFC;
                color: #64748B;
                font-size: 12px;
                text-transform: uppercase;
                border-bottom: 2px solid #E2E8F0;
            }
            .bill-table td {
                padding: 12px;
                border-bottom: 1px solid #E2E8F0;
                color: #333;
                font-size: 14px;
            }
            .bill-table .amount-col { text-align: right; font-weight: 600; }
            .total-section {
                margin-top: 20px;
                text-align: right;
            }
            .total-label { font-size: 14px; color: #64748B; margin-right: 10px; }
            .total-value { font-size: 24px; color: #0F172A; font-weight: bold; }
            .status-badge {
                display: inline-block;
                padding: 6px 12px;
                background-color: #d1fae5;
                color: #065f46;
                border-radius: 20px;
                font-size: 12px;
                font-weight: bold;
                text-transform: uppercase;
                margin-top: 10px;
            }
            .footer {
                margin-top: 60px;
                text-align: center;
                font-size: 10px;
                color: #94A3B8;
                border-top: 1px solid #E2E8F0;
                padding-top: 20px;
            }
            @media print {
                body { padding: 20px; }
                @page { margin: 0.5in; }
            }
        </style>
    </head>
    <body>
        <div class="header-container">
            <div class="logo-section">
                <img src="${logoUrl}" class="logo-img" alt="Logo">
            </div>
            <div class="title-section">
                <h1>Resido</h1>
                <h2>Official Payment Receipt</h2>
                <p>Receipt generated on ${date}</p>
            </div>
        </div>

        <div class="content-grid">
            <div>
                <div class="box-title">Student Information</div>
                <div class="info-row"><span class="info-label">Name:</span> <span class="info-val">${userData?.name || 'N/A'}</span></div>
                <div class="info-row"><span class="info-label">Course:</span> <span class="info-val">${userData?.course || 'N/A'}</span></div>
                <div class="info-row"><span class="info-label">Batch:</span> <span class="info-val">${userData?.batch || 'N/A'}</span></div>
                <div class="info-row"><span class="info-label">Email:</span> <span class="info-val">${userData?.email || 'N/A'}</span></div>
                <div class="info-row"><span class="info-label">Contact:</span> <span class="info-val">${userData?.contact || 'N/A'}</span></div>
                <div class="info-row"><span class="info-label">Hostel:</span> <span class="info-val">${userData?.hostel || 'N/A'}</span></div>
                <div class="info-row"><span class="info-label">Diet:</span> <span class="info-val">${userData?.diet || 'N/A'}</span></div>
            </div>
            <div style="text-align: right;">
                <div class="box-title" style="text-align: right;">Receipt Details</div>
                <div class="info-row">Receipt Date: <span class="info-val">${date}</span></div>
                <div class="info-row">Order ID: <span class="info-val">${bill.paymentId || 'N/A'}</span></div>
                <div class="info-row">Txn ID: <span class="info-val">${bill.transactionId || 'N/A'}</span></div>
                <div class="info-row">Method: <span class="info-val">${bill.paymentMethod || 'Online'}</span></div>
                <div class="info-row">Category: <span class="info-val">${formatPaymentCategory(bill.paymentCategory)}</span></div>
                <div class="info-row">Paid By: <span class="info-val">${bill.paidBy || 'Self'}</span></div>
                ${loanSection}
                <div class="status-badge">Payment Successful</div>
            </div>
        </div>

        <table class="bill-table">
            <thead>
                <tr>
                    <th>Description</th>
                    <th class="amount-col">Amount (INR)</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>Mess Bill for <strong>${monthYear}</strong></td>
                    <td class="amount-col">₹${bill.billAmount || 0}</td>
                </tr>
                ${fineRow}
            </tbody>
        </table>

        <div class="total-section">
            <span class="total-label">Total Amount Paid:</span>
            <span class="total-value">₹${paidAmount}</span>
        </div>

        <div class="footer">
            <p>Thank you for your payment!</p>
            <p>This is a computer-generated receipt and does not require a physical signature.</p>
            <p>Generated via Resido Web Dashboard</p>
        </div>
    </body>
    </html>
  `;
}

/**
 * Open receipt in a new window for printing / PDF download.
 */
export function downloadReceiptAsPdf(bill, userData, monthYear) {
    const html = generateReceiptHtml(bill, userData, monthYear);
    const printWindow = window.open('', '_blank', 'width=700,height=900');
    if (!printWindow) {
        alert('Please allow pop-ups to download the receipt.');
        return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    // Wait for content to render, then trigger print
    printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
    };
    // Fallback if onload doesn't fire
    setTimeout(() => {
        printWindow.focus();
        printWindow.print();
    }, 500);
}

/**
 * Render receipt HTML into a hidden container and capture as canvas.
 * Returns a Blob (image/png) or null on failure.
 */
async function renderReceiptToCanvas(bill, userData, monthYear) {
    const html2canvas = (await import('html2canvas')).default;

    // Create hidden container
    const container = document.createElement('div');
    container.style.cssText = 'position:fixed;left:-9999px;top:0;width:595px;background:#fff;z-index:-1;';
    document.body.appendChild(container);

    // Write receipt HTML into container via iframe-like approach
    const html = generateReceiptHtml(bill, userData, monthYear);
    // Extract body content (between <body> and </body>)
    const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    const styleMatch = html.match(/<style[^>]*>([\s\S]*?)<\/style>/i);

    if (styleMatch) {
        const styleEl = document.createElement('style');
        styleEl.textContent = styleMatch[1];
        container.appendChild(styleEl);
    }

    if (bodyMatch) {
        const contentDiv = document.createElement('div');
        contentDiv.innerHTML = bodyMatch[1];
        container.appendChild(contentDiv);
    } else {
        container.innerHTML = html;
    }

    // Apply base styles to container
    container.style.fontFamily = "'Helvetica', sans-serif";
    container.style.color = '#333';
    container.style.padding = '40px';
    container.style.boxSizing = 'border-box';

    // Wait for images to load
    const images = container.querySelectorAll('img');
    await Promise.all(Array.from(images).map(img =>
        new Promise(resolve => {
            if (img.complete) resolve();
            else { img.onload = resolve; img.onerror = resolve; }
        })
    ));

    // Small delay for rendering
    await new Promise(r => setTimeout(r, 200));

    try {
        const canvas = await html2canvas(container, {
            scale: 2, // Retina quality
            useCORS: true,
            backgroundColor: '#ffffff',
            width: 595,
            logging: false,
        });

        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                document.body.removeChild(container);
                resolve(blob);
            }, 'image/png');
        });
    } catch (err) {
        console.error('html2canvas error:', err);
        document.body.removeChild(container);
        return null;
    }
}

/**
 * Download receipt as a PNG image file.
 */
export async function downloadReceiptAsImage(bill, userData, monthYear) {
    const blob = await renderReceiptToCanvas(bill, userData, monthYear);
    if (!blob) {
        // Fallback to PDF
        downloadReceiptAsPdf(bill, userData, monthYear);
        return;
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Resido_Receipt_${monthYear || 'receipt'}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

/**
 * Share receipt as PNG image using Web Share API (with fallback to download).
 * Mirrors Android's shareReceipt() which generates an image from HTML.
 */
export async function shareReceipt(bill, userData, monthYear) {
    // Try image-based sharing first
    const blob = await renderReceiptToCanvas(bill, userData, monthYear);

    if (blob && navigator.canShare) {
        const file = new File([blob], `Resido_Receipt_${monthYear || 'receipt'}.png`, { type: 'image/png' });
        const shareData = {
            title: `Resido Receipt - ${monthYear}`,
            files: [file],
        };

        if (navigator.canShare(shareData)) {
            try {
                await navigator.share(shareData);
                return true;
            } catch (err) {
                if (err.name !== 'AbortError') {
                    console.error('Image share failed:', err);
                }
                return false;
            }
        }
    }

    // Fallback: text-only share via Web Share API
    if (navigator.share) {
        try {
            await navigator.share({
                title: `Resido Receipt - ${monthYear}`,
                text: `Payment receipt for ${monthYear}. Amount: ₹${((bill.paidAmount || 0) + (bill.loanDeductionApplied || 0)).toFixed(2)}. Order ID: ${bill.paymentId || 'N/A'}.`,
            });
            return true;
        } catch (err) {
            if (err.name !== 'AbortError') {
                console.error('Share failed:', err);
            }
            return false;
        }
    }

    // Final fallback: download PNG or PDF
    if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Resido_Receipt_${monthYear || 'receipt'}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    } else {
        downloadReceiptAsPdf(bill, userData, monthYear);
    }
    return true;
}

